import { Button, Divider, Slider } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import DefaultDropDown from './DefaultDropDown';

interface StepDefinition {
    max: number;
    step: number;
}

interface YearRangeFilterProps {
    title: string;
    value: [number | undefined, number | undefined];
    minYear?: number;
    maxYear?: number;
    steps: StepDefinition[];
    onChanged: (value: [number | undefined, number | undefined]) => void;
    'data-cy'?: string;
}

// Function to generate step values dynamically based on provided step definitions
const generateStepValues = (steps: StepDefinition[], minYear: number) => {
    let values: number[] = [minYear]; // Start from minYear instead of 0
    let current = minYear;

    for (const range of steps) {
        while (current + range.step <= range.max) {
            current += range.step;
            values.push(current);
        }
    }

    return values;
};

const mapSliderToValue = (sliderIndex: number, stepValues: number[]) => stepValues[sliderIndex];

const mapValueToSlider = (actualValue: number, stepValues: number[]) => {
    const index = stepValues.findIndex((val) => val >= actualValue);
    return index === -1 ? stepValues.length - 1 : index;
};

const YearRangeFilter = (props: YearRangeFilterProps) => {
    const { title, value, minYear = 1800, maxYear = 2024, steps, onChanged, 'data-cy': dataCy } = props;

    const STEP_VALUES = generateStepValues(steps, minYear);

    const [rawRange, setRawRange] = useState<[number | undefined, number | undefined]>(value);

    // Convert undefined values to minYear for slider calculations
    const sliderMin = rawRange[0] ?? minYear;
    const sliderMax = rawRange[1] ?? maxYear;
    const sliderRange = [mapValueToSlider(sliderMin, STEP_VALUES), mapValueToSlider(sliderMax, STEP_VALUES)] as [number, number];

    const childRef = useRef<{ closePopup: () => void }>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [headerTitle, setHeaderTitle] = useState<string>(title);

    useEffect(() => {
        setRawRange(value);

        let caption = title;
        if (value[0] !== undefined || value[1] !== undefined) {
            if (value[0] !== undefined && value[1] === undefined) {
                caption = `> ${value[0]}`;
            } else if (value[0] === undefined && value[1] !== undefined) {
                caption = `< ${value[1]}`;
            } else if (value[0] !== undefined && value[1] !== undefined) {
                caption = `${value[0]} ~ ${value[1]}`;
            }
        }
        setHeaderTitle(caption);
    }, [value, title]);

    const handleSliderChange = (_: Event, newValue: number | number[]) => {
        if (Array.isArray(newValue)) {
            const newSliderRange: [number, number] = [newValue[0], newValue[1]];
            const actualMin = mapSliderToValue(newSliderRange[0], STEP_VALUES);
            const actualMax = mapSliderToValue(newSliderRange[1], STEP_VALUES);
            setRawRange([actualMin, actualMax]);
        }
        setErrorMessage(null);
    };

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
        const cleaned = e.target.value.replace(/[^0-9]/g, '');
        const val = cleaned ? Number(cleaned) : undefined;

        setRawRange((prev) => {
            const newValue: [number | undefined, number | undefined] = idx === 0 ? [val, prev[1]] : [prev[0], val];
            return newValue;
        });
        setErrorMessage(null);
    };

    const onApplyClicked = () => {
        if (rawRange[1] !== undefined && rawRange[0] !== undefined && rawRange[0] > rawRange[1]) {
            setErrorMessage('Minimum year cannot be greater than Maximum year.');
            return;
        }

        onChanged(rawRange);

        if (childRef.current) {
            childRef.current.closePopup();
        }
    };

    const onClearClicked = () => {
        setRawRange([undefined, undefined]);
        onChanged([undefined, undefined]);
    };

    const getDisplayValue = (val: number | undefined) => {
        return val ? val.toString() : '';
    };

    const isEmpty = rawRange[0] === undefined && rawRange[1] === undefined;

    return (
        <DefaultDropDown ref={childRef} Title={<div className="truncate">{headerTitle}</div>} IsEmpty={isEmpty} onClearClicked={onClearClicked}>
            <div className="flex flex-col gap-[20px] text-[12px] mt-[5px]">
                <div className="h-[60px] flex flex-col justify-between px-[20px]">
                    <div className="text-[20px] font-semibold">{title}</div>
                    <div className="font-medium text-[14px]">{'Select Year Range'}</div>
                </div>
                <div className="px-[20px]">
                    <Slider
                        getAriaLabel={() => `${title} range`}
                        value={sliderRange}
                        onChange={handleSliderChange}
                        getAriaValueText={(v) => `${mapSliderToValue(v, STEP_VALUES)}`}
                        max={STEP_VALUES.length - 1}
                        min={0}
                        size="small"
                        step={1}
                        valueLabelDisplay="on"
                        valueLabelFormat={(v) => `${mapSliderToValue(v, STEP_VALUES)}`}
                        slotProps={{
                            valueLabel: {
                                className: 'bg-black',
                            },
                        }}
                        data-cy={dataCy}
                    />
                </div>

                <div className="grid grid-cols-2 gap-[26px] px-[20px]">
                    <div>
                        <div className="font-medium pb-[8px]">From Year</div>
                        <div className="flex gap-[5px] justify-between border border-my-gray rounded-[5px] h-[36px] items-center px-[5px]">
                            <input
                                type="text"
                                className="w-full px-[5px]"
                                value={getDisplayValue(rawRange[0])}
                                onChange={(e) => onInputChange(e, 0)}
                                placeholder={minYear.toString()}
                                data-cy="min-input"
                            />
                        </div>
                    </div>

                    <div>
                        <div className="font-medium pb-[8px]">To Year</div>
                        <div className="flex gap-[5px] justify-between border border-my-gray rounded-[5px] h-[36px] items-center px-[5px]">
                            <input
                                type="text"
                                className="w-full px-[5px]"
                                value={getDisplayValue(rawRange[1])}
                                onChange={(e) => onInputChange(e, 1)}
                                placeholder={maxYear.toString()}
                                data-cy="max-input"
                            />
                        </div>
                    </div>
                </div>

                {errorMessage && <div className="text-red-500 text-[12px] mx-auto">{errorMessage}</div>}

                <Divider className="w-full" />

                <div className="pb-[16px] flex justify-between px-[20px]">
                    <Button onClick={onClearClicked} data-cy="clear-button">
                        Clear
                    </Button>
                    <Button variant="contained" color="primary" onClick={onApplyClicked} data-cy="apply-button">
                        Apply
                    </Button>
                </div>
            </div>
        </DefaultDropDown>
    );
};

export default YearRangeFilter;
